import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from 'sonner';
import { evaluateContaDocumentacao, type ContaDocFields } from '@/utils/documentacaoContabil';

interface Props {
  conta: ContaDocFields & { id: string; status?: string };
  tipoConta: 'pagar' | 'receber';
  /** Só mostra badge se liquidada (pago/recebido) */
  onlyIfLiquidada?: boolean;
  onUpdated?: () => void;
}

export function ContaDocumentacaoBadge({
  conta,
  tipoConta,
  onlyIfLiquidada = true,
  onUpdated,
}: Props) {
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [exBoleto, setExBoleto] = useState(false);
  const [exNf, setExNf] = useState(false);
  const [saving, setSaving] = useState(false);

  const liquidada =
    tipoConta === 'pagar' ? conta.status === 'pago' : conta.status === 'recebido';
  if (onlyIfLiquidada && !liquidada) return null;

  const { faltantes, completa, labelFaltantes } = evaluateContaDocumentacao(conta);
  if (completa) {
    return (
      <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-700 dark:text-emerald-400">
        Docs OK
      </Badge>
    );
  }

  const salvarExcecao = async () => {
    if (!exBoleto && !exNf) {
      toast.error('Marque pelo menos uma exceção (sem boleto e/ou sem NF)');
      return;
    }
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/contabilidade/excecao/${tipoConta}/${conta.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boleto: exBoleto ? true : undefined,
          nota_fiscal: exNf ? true : undefined,
          motivo: motivo || 'Exceção marcada na UI',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Falha ao salvar exceção');
      }
      toast.success('Exceção de documentação salva');
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="inline-flex"
        onClick={(e) => {
          e.stopPropagation();
          setExBoleto(faltantes.includes('boleto'));
          setExNf(faltantes.includes('nota_fiscal'));
          setOpen(true);
        }}
      >
        <Badge variant="destructive" className="text-[10px] cursor-pointer">
          Falta: {labelFaltantes}
        </Badge>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Documentação contábil</DialogTitle>
            <DialogDescription>
              Faltam: {labelFaltantes}. Se a conta realmente não tem boleto ou NF, marque a
              exceção para liberar a prontidão.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {faltantes.includes('boleto') && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={exBoleto} onCheckedChange={(v) => setExBoleto(Boolean(v))} />
                Não possui boleto
              </label>
            )}
            {faltantes.includes('nota_fiscal') && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={exNf} onCheckedChange={(v) => setExNf(Boolean(v))} />
                Não possui nota fiscal
              </label>
            )}
            {faltantes.includes('comprovante') && (
              <p className="text-sm text-muted-foreground">
                Comprovante é obrigatório nesta regra — anexe no diálogo de documentos.
              </p>
            )}
            <div className="space-y-1">
              <Label htmlFor="motivo-excecao">Motivo</Label>
              <Textarea
                id="motivo-excecao"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ex.: fornecedor não emitiu NF neste pagamento"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarExcecao} disabled={saving}>
              Salvar exceção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
